import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives";

import { usePriceFinderContext } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { wacToken } from "@/libraries/design/foundations";

import { PricingPeriod, TRACKING_EVENT_NAMES } from "../constants";
import { usePriceFinderStore } from "../store/store";

const labelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "1rem",
    letterSpacing: ".3px",
    fontWeight: "600",
    whiteSpace: "nowrap"
});

const legendStyles = css({
    float: "left"
});

const inputTermStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#091E42",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "1rem",
    letterSpacing: ".3px",
    fontWeight: "400",
    flex: 1
});

const radioLabelStyles = css({
    cursor: "pointer",
    gap: "8px",
    display: "flex",
    alignItems: "center"
});

const radioStyles = css({
    "backgroundColor": "#fafbfc",
    "border": "calc(2px * 12 / 7) solid #DFE1E6",
    "borderRadius": "50%",
    "cursor": "pointer",
    "display": "flex",
    "width": "24px",
    "height": "24px",
    "margin": 0,
    "position": "relative",
    "alignItems": "center",
    "justify-content": "center",
    "flexShrink": 0,
    "outline": "none",
    "transform": "scale(calc(.583333))",
    "transition":
        "border-color 0.2s ease-in-out 0s, background-color 0.2s ease-in-out 0s",
    "verticalAlign": "top",
    "appearance": "none",
    ":focus": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "#4c9aff"
    },
    ":active, :hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#EBECF0"
    },
    "::after": {
        width: "calc(6.85714px)",
        height: "calc(6.85714px)",
        position: "absolute",
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#fafbfc",
        borderRadius: "50%",
        content: "",
        opacity: 0,
        transition:
            "background-color 0.2s ease-in-out 0s, opacity 0.2s ease-in-out 0s"
    },
    ":checked": {
        "borderColor": "#0052cc",
        "backgroundColor": "#0052cc",
        ":hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#0065ff"
        },
        "::after": {
            opacity: 1
        }
    },
    [media.above.sm]: {
        "backgroundColor": "#fff",
        "borderColor": "#0052cc",
        "borderWidth": "2px",
        "transform": "none",
        ":focus-visible": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "#0052cc 0px 0px 3px 3px"
        },
        "::after": {
            width: "14px",
            height: "14px",
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#0052cc"
        },
        ":checked": {
            "backgroundColor": "#fff",
            ":hover": {
                backgroundColor: "unset"
            }
        }
    }
});

const discountLozengeStyles = css({
    width: "fit-content",
    margin: "auto",
    paddingInlineStart: "4px",
    textTransform: "uppercase",
    [media.below.sm]: {
        maxWidth: "220px",
        width: "100%",
        margin: "0",
        marginInlineStart: "4px",
        textAlign: "right"
    }
});

const billMeStyles = css({
    height: "100%",
    display: "flex",
    gap: "16px",

    [media.below.xs]: {
        flexWrap: "wrap"
    }
});

export const PriceFinderInputsRedesign = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [{ unitCount, billingCycle, messages }, actions] =
        usePriceFinderStore();

    const { isMonthlyBillingOnly, isAnnualBillingOnly, dataProductKey } =
        usePriceFinderContext();

    useEffect(() => {
        if (isAnnualBillingOnly) {
            actions.updateBillingCycle("ANNUAL");
        }
    }, []);

    const [visualUnitCount, setVisualUnitCount] = useState<number | "">(
        unitCount
    );

    useEffect(() => {
        if (
            visualUnitCount !== "" &&
            visualUnitCount > 0 &&
            !Number.isNaN(visualUnitCount)
        ) {
            actions.updateUnitCount(visualUnitCount);
        }
    }, [visualUnitCount, actions]);

    const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const stringValue = e.target.value;

        if (stringValue === "") return setVisualUnitCount("");

        // validate max number of digits
        // default is 5
        // TODO: make this configurable
        if (stringValue.length > 6) return;

        const numberValue = parseInt(stringValue, 10);

        if (numberValue < 1) return;
        setVisualUnitCount(numberValue);
    };

    const handleUnitCountBlurChange = () => {
        if (visualUnitCount === "") setVisualUnitCount(unitCount);
        const data = { unitCount, productKey: dataProductKey };
        createAnalyticsEvent({
            eventType: "ui",
            eventName: `${TRACKING_EVENT_NAMES.unitTierBox}_blurred`,
            eventComponent: "input",
            event: "blurred",
            actionSubject: TRACKING_EVENT_NAMES.unitTierBox,
            action: "blurred",
            attributes: data,
            data
        }).fire();
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const data = {
            billingCycle: e.target.id,
            productKey: dataProductKey
        };
        createAnalyticsEvent({
            eventType: "ui",
            eventName: TRACKING_EVENT_NAMES.billingCycleChanged,
            eventComponent: "radioButton",
            event: "clicked",
            actionSubject: TRACKING_EVENT_NAMES.billingCycleChanged,
            action: "clicked",
            attributes: data
        }).fire();

        actions.updateBillingCycle(e.target.id as PricingPeriod);
    };

    return (
        <div
            css={css({
                margin: "auto",
                maxWidth: "1408px"
            })}
        >
            <div
                css={css({
                    maxWidth: "400px",
                    margin: "auto",
                    [media.above.sm]: {
                        maxWidth: "100%"
                    },
                    [media.below.md]: {
                        margin: "0 0 auto"
                    }
                })}
            >
                <div
                    css={[
                        css({
                            maxWidth: "400px",
                            marginBlockEnd: "24px",
                            [media.above.sm]: {
                                display: "flex",
                                maxWidth: "100%",
                                justifyContent: "center",
                                gap: "80px"
                            }
                        })
                    ]}
                >
                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            [media.above.sm]: {
                                display: "block"
                            }
                        })}
                    >
                        <label
                            css={[
                                labelStyles,
                                css({
                                    marginInlineEnd: 8,
                                    color: wacToken("color.primary.black")
                                })
                            ]}
                            htmlFor="unit-count"
                        >
                            <FormattedMessage
                                {...messages.unitCountInputTextRedesign}
                            />
                        </label>
                        <div
                            css={css({
                                padding: "16px 0",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "fit-content",
                                [media.above.sm]: {
                                    margin: 0,
                                    padding: 0,
                                    display: "inline-flex",
                                    flex: 1
                                }
                            })}
                        >
                            <input
                                id="unit-count"
                                type="number"
                                aria-describedby="unit-count-term"
                                css={css({
                                    "color": "#172B4D",
                                    "fontWeight": "400",
                                    "fontSize": "1rem",
                                    "lineHeight": "24px",
                                    "marginInlineEnd": "5px",
                                    "padding": "8px 6px",
                                    "width": "75px",
                                    "borderRadius": "4px",
                                    "background": "#F4F5F7",
                                    "border": "none",
                                    "-moz-appearance": "textfield",
                                    "::-webkit-inner-spin-button, ::-webkit-outer-spin-button":
                                        {
                                            "-webkit-appearance": "none"
                                        }
                                })}
                                value={visualUnitCount}
                                onChange={handleUnitCountChange}
                                onBlur={handleUnitCountBlurChange}
                                data-event-component="inputNumber"
                                data-event-container="priceBlock"
                                data-event="clicked"
                            />
                        </div>
                        <span id="unit-count-term" css={inputTermStyle}>
                            <FormattedMessage
                                {...messages.unitCountInputTerm}
                            />
                        </span>
                    </div>
                    {!isMonthlyBillingOnly && !isAnnualBillingOnly && (
                        <>
                            <fieldset
                                css={css({
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "16px",
                                    border: "none"
                                })}
                                data-testid="billing-inputs"
                            >
                                <legend css={[labelStyles, legendStyles]}>
                                    <FormattedMessage
                                        id="Pricing.priceFinderInputs.billMeText"
                                        defaultMessage="Bill me:"
                                    />
                                </legend>
                                <span css={billMeStyles}>
                                    <label
                                        css={radioLabelStyles}
                                        htmlFor="MONTHLY"
                                    >
                                        <input
                                            css={radioStyles}
                                            type="radio"
                                            id="MONTHLY"
                                            data-testid="billing-radio-monthly"
                                            name="billing"
                                            checked={billingCycle === "MONTHLY"}
                                            onChange={handleRadioChange}
                                        />
                                        <FormattedMessage
                                            id="Pricing.priceFinderInputs.monthly"
                                            defaultMessage="Monthly"
                                        />
                                    </label>
                                    <span
                                        css={css({
                                            display: "flex",
                                            flexWrap: "nowrap"
                                        })}
                                    >
                                        <label
                                            css={[radioLabelStyles]}
                                            htmlFor="ANNUAL"
                                        >
                                            <input
                                                css={radioStyles}
                                                type="radio"
                                                id="ANNUAL"
                                                data-testid="billing-radio-annual"
                                                name="billing"
                                                checked={
                                                    billingCycle === "ANNUAL"
                                                }
                                                onChange={handleRadioChange}
                                            />
                                            <FormattedMessage
                                                id="Pricing.priceFinderInputs.anually"
                                                defaultMessage="Annually"
                                            />
                                        </label>
                                        <span css={discountLozengeStyles}>
                                            <Lozenge appearance="inprogress">
                                                <FormattedMessage
                                                    id="Pricing.priceFinderInputs.saveUpTo"
                                                    defaultMessage="SAVE UP TO 17%"
                                                />
                                            </Lozenge>
                                        </span>
                                    </span>
                                </span>
                            </fieldset>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
